import { CookieUtils } from './cookieUtils';
import { getLinksFromUnescapeHTML, unescapeHTML } from '../../utils';
import { getSessionPlayer } from '../../commons/getSessionPlayerRequire';
import type { Cookie } from './cookieUtils';
import type { DynamicParentOptions } from 'clickim/src/background/codeSnippets/getNextDynamicParent';
import type { WebDriver } from '../webdriver';
import type { SeleniumTarget } from '../stepActions/stepAction';
import type { FrameHandler } from 'clickim/src/locators/dynamicParentUtils';
import type { TestimServicesApi } from 'clickim/src/common/TestimServicesApi';
import type { IStepActionUtils } from 'clickim/src/background/stepActions/stepActionUtils';
import type { VisibilityInfo } from 'clickim/src/background/visibilityUtils';


export const KEY_MAP = {
    8: '\uE008', // (Backspace)
    9: '\uE004', // (tab)
    13: '\uE007', // (enter)
    27: '\uE00C', // (esc)
    33:	'\uE00E', // (page up)
    34: '\uE00F', // (page down)
    35: '\uE010', // (end)
    36: '\uE011', // (home)
    45:	'\uE016', // (insert)
    112: '\uE031', // (f1)
    113: '\uE032', // (f2)
    114: '\uE033', // (f3)
    115: '\uE034', // (f4)
    116: '\uE035', // (f5)
    117: '\uE036', // (f6)
    118: '\uE037', // (f7)
    119: '\uE038', // (f8)
    120: '\uE039', // (f9)
    121: '\uE03A', // (f10)
    122: '\uE03B', // (f11)
    123: '\uE03C', // (f12)
};

export class StepActionUtils implements IStepActionUtils {
    // @ts-expect-error value is set in the sessionPlayer's constructor
    public readonly testimServicesApi: TestimServicesApi;
    private readonly _abortedSteps: any[] = [];
    private readonly cookieUtils: CookieUtils;

    constructor(public readonly driver: WebDriver) {
        this.cookieUtils = new CookieUtils(this.driver);
    }

    private get sessionPlayerInit() {
        return getSessionPlayer();
    }

    resetAbort() {
        this._abortedSteps.length = 0;
    }

    abort(result: any) {
        this._abortedSteps.push(result);
    }

    get abortedSteps() {
        return this._abortedSteps;
    }

    getClickOffset(clickOffset: { x: number; y: number } | undefined, rect: VisibilityInfo) {
        const { utils, positionUtils } = this.sessionPlayerInit;
        const elementCenter = positionUtils.calculateElementMiddlePoint(rect);

        if (
            clickOffset &&
            utils.isWithinBounds(rect.left, rect.left + rect.width, clickOffset.x) &&
            utils.isWithinBounds(rect.top, rect.top + rect.height, clickOffset.y)
        ) {
            return {
                x: clickOffset.x - elementCenter.x,
                y: clickOffset.y - elementCenter.y,
            };
        }
        return {
            x: 0,
            y: 0,
        };
    }

    async executeInAut(context: any, code: string) {
        const { value } = await this.driver.executeJS(code);
        return value;
    }

    extractTargetText(target: SeleniumTarget) {
        return this.driver.getTargetText(target);
    }

    extractText(locatedElement: any) { // unused?
        return this.driver.getElementTextJS(locatedElement);
    }

    async markDynamicParent(target: SeleniumTarget, id: string) {
        await this.driver.markDynamicParent(target, id);
    }

    getCookie(name: string) {
        return this.cookieUtils.get({ name });
    }

    setCookie(cookieObject: Cookie) {
        return this.cookieUtils.set(cookieObject);
    }

    async getNextDynamicParent(frameHandler: FrameHandler, dynamicParentOptions: DynamicParentOptions) {
        const code = this.sessionPlayerInit.codeSnippets.getNextDynamicParent(dynamicParentOptions);
        const { value } = await this.driver.executeJS(`return (function() { ${code} })();`);
        return value;
    }

    public unescapeHTML = unescapeHTML;

    public getLinksFromUnescapeHTML = getLinksFromUnescapeHTML;
}
